import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  ArrowLeft, Phone, Shield, AlertTriangle, Scale, BookOpen,
  ExternalLink, Heart, FileText, ChevronDown, ChevronUp
} from "lucide-react";
import axios from "axios";
import Navigation from "@/components/Navigation";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ResourcesPage = () => {
  const [hotlines, setHotlines] = useState([]);
  const [laws, setLaws] = useState([]);
  const [disclaimer, setDisclaimer] = useState("");
  const [expandedLaw, setExpandedLaw] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/resources/emergency`),
      axios.get(`${API}/resources/laws`)
    ]).then(([emergRes, lawsRes]) => {
      setHotlines(emergRes.data.hotlines || []);
      setDisclaimer(emergRes.data.disclaimer || "");
      setLaws(lawsRes.data.laws || []);
    }).catch(console.error).finally(() => setLoading(false));
  }, []);

  const categoryIcons = {
    "Domestic Violence": Shield,
    "Suicide & Crisis": AlertTriangle,
    "Poison Control": AlertTriangle,
    "Addiction & Substance Abuse": Heart,
    "Child Abuse": Shield,
    "Adult Protective Services": Shield,
    "Mental Health Crisis": Heart,
    "Text Support": Phone,
    "General Help": Phone,
    "Sexual Assault": Shield,
  };

  const categoryColors = {
    "Domestic Violence": "bg-red-50 text-red-600 border-red-200",
    "Suicide & Crisis": "bg-amber-50 text-amber-600 border-amber-200",
    "Poison Control": "bg-orange-50 text-orange-600 border-orange-200",
    "Addiction & Substance Abuse": "bg-purple-50 text-purple-600 border-purple-200",
    "Child Abuse": "bg-rose-50 text-rose-600 border-rose-200",
    "Adult Protective Services": "bg-blue-50 text-blue-600 border-blue-200",
    "Mental Health Crisis": "bg-indigo-50 text-indigo-600 border-indigo-200",
    "Text Support": "bg-teal-50 text-teal-600 border-teal-200",
    "General Help": "bg-sky-50 text-sky-600 border-sky-200",
    "Sexual Assault": "bg-pink-50 text-pink-600 border-pink-200",
  };

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      <Navigation />

      <section className="pt-28 pb-24 px-6 lg:px-12" data-testid="resources-section">
        <div className="container mx-auto max-w-5xl">
          <Link to="/" className="inline-flex items-center gap-2 text-[#4FB3D4] hover:text-[#4FB3D4]/80 mb-8">
            <ArrowLeft className="h-4 w-4" /> Back to Home
          </Link>

          {/* Emergency Banner */}
          <div className="bg-red-50 border-2 border-red-200 rounded-2xl p-6 mb-8" data-testid="emergency-banner">
            <div className="flex items-start gap-4">
              <AlertTriangle className="h-6 w-6 text-red-500 flex-shrink-0 mt-1" />
              <div>
                <h2 className="font-heading text-xl font-bold text-red-700 mb-2">If You Are in Immediate Danger</h2>
                <p className="text-red-600 mb-3">Call <strong>911</strong> immediately. Your safety is the priority.</p>
                <div className="flex flex-wrap gap-3">
                  <a href="tel:988" className="inline-flex items-center gap-2 bg-red-600 text-white px-4 py-2 rounded-full text-sm font-medium hover:bg-red-700 transition-colors" data-testid="call-988-btn">
                    <Phone className="h-4 w-4" /> Call 988 (Crisis)
                  </a>
                  <a href="tel:18007997233" className="inline-flex items-center gap-2 bg-red-600 text-white px-4 py-2 rounded-full text-sm font-medium hover:bg-red-700 transition-colors" data-testid="call-dv-btn">
                    <Phone className="h-4 w-4" /> Call DV Hotline
                  </a>
                </div>
              </div>
            </div>
          </div>

          {/* Header */}
          <div className="mb-12">
            <p className="text-sm font-medium uppercase tracking-widest text-[#A69BE0] mb-3">Arkansas Resources</p>
            <h1 className="font-heading text-4xl md:text-5xl font-bold tracking-tight text-[#1A2A3A] mb-4">
              Emergency Resources & Legal Information
            </h1>
            <p className="text-base text-[#1A2A3A]/60 max-w-2xl">
              We compiled these resources because we know how hard it is to search for help when you are in pain. All numbers are current for 2025. You are not alone, and help is available right now.
            </p>
          </div>

          {/* Disclaimer */}
          {disclaimer && (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-8 text-sm text-amber-700" data-testid="resources-disclaimer">
              <AlertTriangle className="h-4 w-4 inline mr-2" />
              {disclaimer}
            </div>
          )}

          {/* Emergency Hotlines */}
          <div className="mb-16">
            <h2 className="font-heading text-2xl font-semibold text-[#1A2A3A] mb-6 flex items-center gap-3">
              <Phone className="h-6 w-6 text-[#4FB3D4]" /> Emergency Hotlines
            </h2>
            {loading ? (
              <div className="grid md:grid-cols-2 gap-4">
                {[1,2,3,4].map(i => (
                  <div key={i} className="animate-pulse h-24 bg-white rounded-xl" />
                ))}
              </div>
            ) : (
              <div className="grid md:grid-cols-2 gap-4">
                {hotlines.map((h, i) => {
                  const Icon = categoryIcons[h.category] || Phone;
                  const colorClass = categoryColors[h.category] || "bg-gray-50 text-gray-600 border-gray-200";
                  return (
                    <Card key={i} className={`rounded-xl border ${colorClass} overflow-hidden`} data-testid={`hotline-${i}`}>
                      <CardContent className="p-5">
                        <div className="flex items-start gap-3">
                          <Icon className="h-5 w-5 flex-shrink-0 mt-0.5" />
                          <div className="flex-1">
                            <p className="text-xs font-semibold uppercase tracking-wider opacity-70 mb-1">{h.category}</p>
                            <h3 className="font-semibold text-[#1A2A3A] mb-1">{h.name}</h3>
                            <a href={`tel:${h.number.replace(/\D/g, '')}`} className="text-lg font-bold text-[#1A2A3A] hover:text-[#4FB3D4] transition-colors" data-testid={`hotline-number-${i}`}>
                              {h.number}
                            </a>
                            <p className="text-xs text-[#1A2A3A]/60 mt-1">{h.description}</p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </div>

          {/* Arkansas Laws */}
          <div className="mb-16">
            <h2 className="font-heading text-2xl font-semibold text-[#1A2A3A] mb-6 flex items-center gap-3">
              <Scale className="h-6 w-6 text-[#A69BE0]" /> Arkansas State & Federal Laws
            </h2>
            <p className="text-sm text-[#1A2A3A]/60 mb-6">
              Understanding your legal rights is a powerful step toward safety and justice. Below is a summary of key laws that protect survivors of abuse in Arkansas. This is not legal advice; please consult a qualified attorney.
            </p>
            <div className="space-y-4">
              {laws.map((law, i) => (
                <Card key={i} className="bg-white rounded-xl shadow-card border border-[#E5E7EB]/50 overflow-hidden" data-testid={`law-card-${i}`}>
                  <button
                    className="w-full p-5 flex items-start justify-between gap-4 text-left"
                    onClick={() => setExpandedLaw(expandedLaw === i ? null : i)}
                    data-testid={`law-toggle-${i}`}
                  >
                    <div className="flex items-start gap-3">
                      <Scale className="h-5 w-5 text-[#A69BE0] flex-shrink-0 mt-0.5" />
                      <div>
                        <h3 className="font-semibold text-[#1A2A3A] mb-1">{law.title}</h3>
                        <p className="text-sm text-[#1A2A3A]/60">{law.summary}</p>
                      </div>
                    </div>
                    {expandedLaw === i ? <ChevronUp className="h-5 w-5 text-[#1A2A3A]/40 flex-shrink-0" /> : <ChevronDown className="h-5 w-5 text-[#1A2A3A]/40 flex-shrink-0" />}
                  </button>
                  {expandedLaw === i && (
                    <div className="px-5 pb-5 pt-0">
                      <ul className="space-y-2 ml-8">
                        {law.key_points?.map((point, j) => (
                          <li key={j} className="flex items-start gap-2 text-sm text-[#1A2A3A]/70">
                            <div className="w-1.5 h-1.5 rounded-full bg-[#A69BE0] flex-shrink-0 mt-2" />
                            {point}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </Card>
              ))}
            </div>
          </div>

          {/* CTA */}
          <div className="text-center">
            <p className="text-[#1A2A3A]/60 mb-4">Need someone to talk to right now?</p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link to="/chat">
                <Button className="bg-[#4FB3D4] hover:bg-[#4FB3D4]/90 text-white rounded-full px-6 py-4 font-medium" data-testid="resources-chat-btn">
                  Talk to Hope (Free AI Companion)
                </Button>
              </Link>
              <Link to="/bonus">
                <Button variant="outline" className="rounded-full px-6 py-4 border-2 border-[#4FB3D4]/20 hover:bg-[#4FB3D4]/5 font-medium" data-testid="resources-bonus-btn">
                  Download Free PDF Guide
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-6 lg:px-12 bg-[#1A2A3A] text-white">
        <div className="container mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="font-heading text-2xl font-bold">ThryvLife</div>
            <nav className="flex gap-8">
              <Link to="/" className="text-white/70 hover:text-white transition-colors text-sm">Home</Link>
              <Link to="/chat" className="text-white/70 hover:text-white transition-colors text-sm">AI Companion</Link>
              <Link to="/calendar" className="text-white/70 hover:text-white transition-colors text-sm">Calendar</Link>
            </nav>
            <p className="text-white/40 text-xs">&copy; 2025 ThryvLife. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default ResourcesPage;
